/**
 * Rhythmz — AudioAnalyzer
 * ─────────────────────────
 * Taps the AudioSourceManager's output GainNode → AnalyserNode → AudioFeatures
 * every frame. The generative engine reads these to drive reactivity.
 *
 * (Butterchurn did its own analysis internally; the generative engine needs this.)
 *
 * Bands: bass < ~250Hz, lowMid/mid/highMid, treble > ~6kHz. Plus RMS level,
 * beat impulse (spectral-flux onset), and a locked BPM (autocorrelation).
 */

export interface AudioFeatures {
  bass: number;
  lowMid: number;
  mid: number;
  highMid: number;
  treble: number;
  level: number;   // RMS-ish 0..1
  beat: number;    // impulse ~1 on onset, decays to 0
  bpm: number;     // locked estimate (0 until confident)
}

export const SILENT: AudioFeatures = {
  bass: 0, lowMid: 0, mid: 0, highMid: 0, treble: 0, level: 0, beat: 0, bpm: 0,
};

export class AudioAnalyzer {
  private analyser: AnalyserNode;
  private freq: Uint8Array;
  private time: Uint8Array;

  // smoothed band state
  private s = { bass: 0, lowMid: 0, mid: 0, highMid: 0, treble: 0, level: 0 };
  // beat detection
  private prevBass = 0;
  private beatEnergy = 0;
  private lastBeat = 0;
  private beatTimes: number[] = [];
  private bpm = 0;

  constructor(source: AudioNode, ctx: AudioContext) {
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.8;
    source.connect(this.analyser);
    // Do NOT connect analyser → destination (would double the audio).
    this.freq = new Uint8Array(this.analyser.frequencyBinCount);
    this.time = new Uint8Array(this.analyser.frequencyBinCount);
  }

  /** Sample one frame. `now` = performance.now() in ms. */
  sample(now: number): AudioFeatures {
    this.analyser.getByteFrequencyData(this.freq as Uint8Array<ArrayBuffer>);
    this.analyser.getByteTimeDomainData(this.time as Uint8Array<ArrayBuffer>);

    const n = this.freq.length;
    const band = (lo: number, hi: number): number => {
      const a = Math.max(0, Math.floor(lo * n));
      const b = Math.min(n, Math.ceil(hi * n));
      let sum = 0;
      for (let i = a; i < b; i++) sum += this.freq[i];
      return sum / Math.max(1, b - a) / 255;
    };
    const bass = band(0.0, 0.04);
    const lowMid = band(0.04, 0.1);
    const mid = band(0.1, 0.25);
    const highMid = band(0.25, 0.5);
    const treble = band(0.5, 1.0);

    let rms = 0;
    for (let i = 0; i < this.time.length; i++) {
      const v = (this.time[i] - 128) / 128;
      rms += v * v;
    }
    const level = Math.sqrt(rms / this.time.length);

    const k = 0.5; // extra smoothing
    this.s.bass += (bass - this.s.bass) * k;
    this.s.lowMid += (lowMid - this.s.lowMid) * k;
    this.s.mid += (mid - this.s.mid) * k;
    this.s.highMid += (highMid - this.s.highMid) * k;
    this.s.treble += (treble - this.s.treble) * k;
    this.s.level += (level - this.s.level) * k;

    // beat detection (spectral flux on bass)
    const flux = Math.max(0, bass - this.prevBass);
    this.prevBass = bass;
    this.beatEnergy *= 0.92;
    if (flux > 0.06 && bass > 0.25 && now - this.lastBeat > 220) {
      this.beatEnergy = 1;
      this.recordBeat(now);
      this.lastBeat = now;
    }

    return {
      bass: this.s.bass, lowMid: this.s.lowMid, mid: this.s.mid,
      highMid: this.s.highMid, treble: this.s.treble, level: this.s.level,
      beat: this.beatEnergy, bpm: this.bpm,
    };
  }

  /** Decay toward silence when no audio source is active. */
  idle(): AudioFeatures {
    this.beatEnergy *= 0.92;
    (Object.keys(this.s) as (keyof typeof this.s)[]).forEach((key) => { this.s[key] *= 0.95; });
    return { ...this.s, beat: this.beatEnergy, bpm: this.bpm };
  }

  private recordBeat(now: number): void {
    this.beatTimes.push(now);
    while (this.beatTimes.length && now - this.beatTimes[0] > 4000) this.beatTimes.shift();
    if (this.beatTimes.length < 4) return;
    const intervals: number[] = [];
    for (let i = 1; i < this.beatTimes.length; i++) intervals.push(this.beatTimes[i] - this.beatTimes[i - 1]);
    intervals.sort((a, b) => a - b);
    const median = intervals[Math.floor(intervals.length / 2)];
    if (median > 0) {
      let bpm = 60000 / median;
      while (bpm < 70) bpm *= 2;
      while (bpm > 180) bpm /= 2;
      this.bpm = this.bpm === 0 ? bpm : this.bpm + (bpm - this.bpm) * 0.1;
    }
  }

  dispose(): void {
    this.analyser.disconnect();
  }
}
